import { useState, useEffect, useMemo, useContext } from "react";
import toastError from "../../errors/toastError";
import { format, sub } from "date-fns";
import api from "../../services/api";
import useDebounce from "../useDebounce";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTickets } from "../../services/ticketsApi";

const useTickets = ({
  searchParam,
  tags,
  users,
  pageNumber,
  status,
  date,
  dateStart,
  dateEnd,
  updatedAt,
  showAll,
  queueIds,
  withUnreadMessages,
  whatsappIds,
  statusFilter,
  forceSearch,
  userFilter,
  sortTickets,
  searchOnMessages,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const { user, socket } = useContext(AuthContext);

  const debouncedSearchParam = useDebounce(searchParam, 500);

  const parsedQueueIds = useMemo(() => {
    const normalizeIds = (values) =>
      values
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

    if (Array.isArray(queueIds)) return normalizeIds(queueIds);
    try {
      const arr = JSON.parse(queueIds || "[]");
      return Array.isArray(arr) ? normalizeIds(arr) : [];
    } catch (_) {
      return [];
    }
  }, [queueIds]);

  const userQueueIds = useMemo(() => {
    const queues = Array.isArray(user?.queues) ? user.queues : [];
    return queues
      .map((q) => Number(q?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }, [user?.queues]);

  const effectiveQueueIds = useMemo(() => {
    if (String(user?.profile || "").toLowerCase() === "admin") {
      return parsedQueueIds;
    }
    return parsedQueueIds.length > 0 ? parsedQueueIds : userQueueIds;
  }, [parsedQueueIds, userQueueIds, user?.profile]);

  useEffect(() => {
    setTickets([]);
  }, [
    debouncedSearchParam,
    tags,
    users,
    status,
    date,
    dateStart,
    dateEnd,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    user?.allowGroup,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.allowViewAllClosedTickets,
    user?.profile,
  ]);

  useEffect(() => {
    const handleTicketsChanged = () => {
      setRefreshTick((prev) => prev + 1);
    };

    window.addEventListener("tickets:changed", handleTicketsChanged);
    return () => {
      window.removeEventListener("tickets:changed", handleTicketsChanged);
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    const fetchTickets = async () => {
      if (userFilter === undefined || userFilter === null) {
        try {
          const requestShowAll = Boolean(showAll);
          const { data } = await getTickets({
            params: {
              searchParam: debouncedSearchParam,
              pageNumber,
              tags,
              users,
              status,
              date,
              dateStart,
              dateEnd,
              updatedAt,
              showAll: requestShowAll ? "true" : "false",
              queueIds,
              withUnreadMessages,
              whatsapps: whatsappIds,
              statusFilter,
              sortTickets,
              searchOnMessages,
            },
          });

          if (!isActive) return;

          if (!data || !Array.isArray(data.tickets)) {
            setLoading(false);
            return;
          }

          const canSeeOwnOpenTicket = (ticket) => {
            if (!ticket) return false;
            if (
              user?.allTicket === "enabled" &&
              (ticket?.queueId === null || ticket?.queueId === undefined)
            ) {
              return true;
            }
            if (Number(ticket?.userId) === Number(user?.id)) return true;
            const participants = Array.isArray(ticket?.participants)
              ? ticket.participants
              : [];
            return participants.some((p) => Number(p?.id) === Number(user?.id));
          };

          const postProcess = (arr) => {
            if (status === "closed" && user?.allowGroup === true && !showAll) {
              return arr.filter(
                (t) => t && (t.isGroup === true || t.userId === user?.id)
              );
            }
            if (
              status === "open" &&
              !showAll &&
              user?.profile !== "admin"
            ) {
              return arr.filter((t) => canSeeOwnOpenTicket(t));
            }
            return arr;
          };

          setTickets((prevTickets) => {
            if (pageNumber === 1) {
              return postProcess(data.tickets);
            }
            const prevIds = new Set(prevTickets.map((t) => t && t.id));
            const toAppend = data.tickets.filter(
              (t) => t && t.id && !prevIds.has(t.id)
            );
            return postProcess([...prevTickets, ...toAppend]);
          });

          setHasMore(data.hasMore);
          setCount(data.count);
          setLoading(false);
        } catch (err) {
          if (!isActive) return;
          setLoading(false);
          toastError(err);
        }
      } else {
        try {
          const { data } = await api.get("/dashboard/moments", {
            params: {
              status,
              showAll,
              queueIds,
              dateStart: format(sub(new Date(), { days: 30 }), "yyyy-MM-dd"),
              dateEnd: format(new Date(), "yyyy-MM-dd"),
              userId: userFilter,
            },
          });

          if (!isActive) return;

          let filteredTickets = data.filter(
            (item) => item.userId === userFilter
          );
          setTickets(filteredTickets);
          setHasMore(null);
          setLoading(false);
        } catch (err) {
          if (!isActive) return;
          setLoading(false);
          toastError(err);
        }
      }
    };
    fetchTickets();

    return () => {
      isActive = false;
    };
  }, [
    debouncedSearchParam,
    tags,
    users,
    pageNumber,
    status,
    date,
    dateStart,
    dateEnd,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    userFilter,
    user?.allowGroup,
    user?.id,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.allowViewAllClosedTickets,
    user?.profile,
    refreshTick,
  ]);

  useEffect(() => {
    if (!user?.companyId || !socket) return;
    const companyId = user.companyId;

    const handleTicketUpdate = (data) => {
      const canSeeOwnOpenTicket = (ticket) => {
        if (!ticket) return false;
        if (Number(ticket?.userId) === Number(user?.id)) return true;
        const participants = Array.isArray(ticket?.participants)
          ? ticket.participants
          : [];
        return participants.some((p) => Number(p?.id) === Number(user?.id));
      };

      const canSeeByUserScope = (ticket) => {
        if (!ticket) return false;
        if (String(user?.profile || "").toLowerCase() === "admin") return true;
        if (!status) return true;
        if (showAll) return true;
        if (status === "pending") return true;
        if (status === "open") return canSeeOwnOpenTicket(ticket);
        if (Number(ticket.userId) === Number(user?.id)) return true;
        if (
          ticket.isGroup === true &&
          (user?.allowGroup === true || String(user?.allowGroup) === "true")
        ) {
          return true;
        }
        return false;
      };

      if (data.action === "update") {
        setTickets((prevTickets) => {
          const hasStatusInPayload =
            data?.ticket && Object.prototype.hasOwnProperty.call(data.ticket, "status");
          const hasQueueInPayload =
            data?.ticket && Object.prototype.hasOwnProperty.call(data.ticket, "queueId");
          const matchesCurrentStatus = status
            ? (!hasStatusInPayload || data?.ticket?.status === status)
            : true;
          const matchesQueueIds =
            effectiveQueueIds.length > 0
              ? (!hasQueueInPayload ||
                effectiveQueueIds.includes(Number(data?.ticket?.queueId)))
              : true;
          const matchesUserScope = canSeeByUserScope(data?.ticket);
          const ticketIndex = prevTickets.findIndex(
            (t) => t.id === data.ticket.id
          );
          if (ticketIndex !== -1) {
            if (!matchesCurrentStatus || !matchesQueueIds || !matchesUserScope) {
              const updatedTickets = [...prevTickets];
              updatedTickets.splice(ticketIndex, 1);
              return updatedTickets;
            }
            const updatedTickets = [...prevTickets];
            updatedTickets[ticketIndex] = data.ticket;
            if (sortTickets === "updatedAt") {
              updatedTickets.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            }
            return updatedTickets;
          }
          if (matchesCurrentStatus && matchesQueueIds && matchesUserScope) {
            const newTicketArray = [data.ticket, ...prevTickets];
            if (sortTickets === "updatedAt") {
              newTicketArray.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            }
            return newTicketArray;
          }
          return prevTickets;
        });
      }
      if (data.action === "create") {
        setTickets((prevTickets) => {
          const matchesCurrentStatus = status
            ? data.ticket.status === status
            : true;
          const matchesQueueIds =
            effectiveQueueIds.length > 0
              ? effectiveQueueIds.includes(Number(data.ticket.queueId))
              : true;
          const matchesUserScope = canSeeByUserScope(data?.ticket);

          if (matchesCurrentStatus && matchesQueueIds && matchesUserScope) {
            const newTicketArray = [data.ticket, ...prevTickets];
            if (sortTickets === "updatedAt") {
              newTicketArray.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            }
            return newTicketArray;
          }
          return prevTickets;
        });
      }
      if (data.action === "delete") {
        setTickets((prevTickets) =>
          prevTickets.filter((ticket) => ticket.id !== data.ticketId)
        );
      }
    };

    const eventName = `company-${companyId}-ticket`;
    socket.on(eventName, handleTicketUpdate);

    return () => {
      socket.off(eventName, handleTicketUpdate);
    };
  }, [user?.companyId, socket, sortTickets, status, effectiveQueueIds, showAll, user?.id, user?.allowGroup, user?.profile]);

  const processedTickets = useMemo(() => {
    return tickets;
  }, [tickets]);

  return { tickets: processedTickets, loading, hasMore, count };
};

export default useTickets;
